<template>
  <q-input
    v-if="!$_.isNil(countryCode)"
    ref="deliveryPhone"
    :model-value="phoneInput"
    class="bg-transparent q-pb-none q-pt-none custom-input-padding"
    :label="$t('form.labels.phoneNumber') + (required ? '' : ` (${$t('common.optional')})`)"
    type="tel"
    :borderless="computedReadonly"
    :readonly="computedReadonly"
    bottom-slots
    :rules="[
      val => {
        if (computedReadonly) return true
        if (!required && $_.isEmpty(val)) return true
        return !$_.isEmpty(val) && phoneObject?.isValid() || $t('form.errors.required')
      }
    ]"
    @update:model-value="(val) => updatePhoneInput(val)"
  >
    <template #prepend>
      <q-select
        v-model="countryCode"
        :disable="computedReadonly"
        class="custom-select-padding"
        :options="availableCountryCodes"
        :hide-dropdown-icon="computedReadonly"
        borderless
        dense
      >
        <template #selected-item="scope">
          <div
            v-bind="scope.itemProps"
          >
            <country-flag
              :country="countryCode"
            />
          </div>
        </template>

        <template #option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section avatar>
              <country-flag
                :country="scope.opt"
              />
            </q-item-section>
            <q-item-section>
              (+{{ getCountryCallingCode(scope.opt) }})
              {{ countryNameMap[scope.opt] }}
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </template>
  </q-input>
</template>

<script>
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js'
import { COUNTRY_CODES } from 'src/i18n'
import { getCountryNameMap } from 'src/i18n/language-regions/utils'

export default {
  name: 'PhoneNumberInput',
  configuration: ['currentCircuit'],
  props: {
    phone: {
      type: String,
      required: false,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    overrideCountryCode: {
      type: String,
      default: null
    },
    maskedByApi: {
      type: Boolean,
      default: false
    }
  },
  emits: ['phone-changed'],
  data () {
    return {
      countryCode: null,
      phoneInput: '',
      phoneObject: null,
      asYouTypeMask: ''
    }
  },
  computed: {
    countryNameMap () {
      return getCountryNameMap()
    },
    computedReadonly () {
      return this.readonly || this.maskedByApi
    },
    availableCountryCodes () {
      return _.sortBy(_.intersection(_.values(COUNTRY_CODES), getCountries()), val => val)
    }
  },
  watch: {
    'countryCode' (val) {
      this.$nextTick(() => {
        if (!this.overrideCountryCode || this.overrideCountryCode !== val) {
          this.updatePhoneInput('')
        }
      })
    },
    'phone' (val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePhoneInput(val)
        })
      }
    },
    'phoneInput' (val) {
      if (!this.computedReadonly) {
        this.$emit('phone-changed', this.phoneObject)
      }
    },
    overrideCountryCode (val) {
      if (val) {
        this.countryCode = val
      }
    }
  },
  beforeMount () {
    this.countryCode = this.overrideCountryCode || this.currentCircuit.iso31661
    if (!_.isEmpty(this.phone)) {
      this.updatePhoneInput(this.phone)
    }
  },
  created () {
    _.extend(this, {
      COUNTRY_CODES,
      getCountryCallingCode
    })
  },
  methods: {
    validate () {
      return this.$refs.deliveryPhone.validate()
    },
    updatePhoneInput (val) {
      if (this.maskedByApi) {
        this.phoneInput = val
        this.phoneObject = null
        return
      }

      val = val.replace(/\D/g, '')
      if (!val || val.length < 2) {
        this.phoneInput = val
        this.phoneObject = null
      } else {
        const parsedPhoneNumber = parsePhoneNumber(val, this.countryCode)
        this.phoneObject = parsedPhoneNumber
        if (parsedPhoneNumber.isValid()) {
          this.phoneInput = parsedPhoneNumber.formatNational()
        } else {
          this.phoneObject = null

          const maskValue = new AsYouType(this.countryCode)
          maskValue.input(val)
          this.phoneInput = maskValue.formattedOutput
        }
      }
    }
  }
}
</script>

<style>
  .custom-select-padding .q-field__native {
    padding-top: 0 !important; /* Remove padding inside the input */
    padding-left: 5px !important;
  }

  .custom-input-padding .q-field__control {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .custom-input-padding .q-field--outlined .q-field__control:before  {
    border: none !important;
  }

  .custom-input-padding .q-field--outlined .q-field__control:after  {
    border: none !important;
  }

  .custom-input-padding .q-field--outlined {
    border: none !important;
    border-radius: 0 !important;
  }

  .custom-input-padding .q-field__prefix {
    font-weight: bold;
  }

  .q-field__append .q-icon {
    padding-right: 10px !important;
  }

</style>
